import { Accordion, Badge, Button, Dialog, Div, P, Space, Span } from '@dnb/eufemia';
import { edit as EditIcon } from '@dnb/eufemia/icons';
import { play as PlayIcon, trash as TrashIcon } from '@dnb/eufemia/icons';
import type { SpacingElementProps } from '@dnb/eufemia/shared/types';
import type { CurrentApiAndScopeEntitlementsDto } from '@portals/shared/portal/AppApiEntitlementDto';
import { AccessTypes, AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import { groupBy } from '@portals/shared-frontend/utils';
import { type JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Card from '@/components/Card';
import ApiTypeBadge from '@/pages/profile/components/ApiTypeBadge';

import ScopeCard from '../ScopeCard';
import RequestApiAccessModal from './components/RequestApiAccessModal';

import style from './index.module.css';
enum EnvironmentAccessLevel {
  NONE = 0,
  TEST = 1,
  LIVE = 2,
}

type EntitlementProps = SpacingElementProps & {
  isAdmin: boolean;
  entitlement: CurrentApiAndScopeEntitlementsDto;
  onDetachApi: (appId: string, apiId: string) => void;
  hasPendingRequest: boolean;
  hasDeniedRequest: boolean;
  app: AppWithEntitlementsDto;
};

const DEVELOPER_PORTAL_API_SLUG = 'developer-portal-api';

export default function Entitlement({
  isAdmin,
  entitlement,
  onDetachApi,
  hasPendingRequest,
  app,
  ...props
}: EntitlementProps): JSX.Element {
  const navigate = useNavigate();

  const currentEnvironmentAccessLevel = entitlement.entitlements.some(({ liveMode }) => liveMode)
    ? 2
    : entitlement.api.approvalStage === 'test' && entitlement?.scopeEntitlements.length === 0
      ? 0
      : 1;

  const [showRequestLiveModeModal, setShowRequestLiveModeModal] = useState(false);

  const canRequestHigherEnvironmentAccessLevel = useMemo(() => {
    switch (currentEnvironmentAccessLevel) {
      case EnvironmentAccessLevel.NONE: {
        return true;
      }
      case EnvironmentAccessLevel.TEST: {
        return entitlement.api.availableInLiveMode;
      }
      case EnvironmentAccessLevel.LIVE: {
        return false;
      }
      default: {
        return false;
      }
    }
  }, [currentEnvironmentAccessLevel, entitlement.api.availableInLiveMode]);

  const hasLiveAccess = entitlement.entitlements.some(({ liveMode }) => liveMode);

  const accessType = entitlement.entitlements.some(({ liveMode }) => liveMode)
    ? 2
    : entitlement.api.approvalStage === 'test' && entitlement?.scopeEntitlements.length === 0
      ? 0
      : 1;

  // Group by name, so we do not get duplicates entitlements mapped
  const scopeEntitlementsByScopeName = groupBy(entitlement?.scopeEntitlements, 'scopeName');

  const apiOrEvent = entitlement.api.type === 'REST' ? 'API' : 'Event';

  const requestHigherEnvironmentAccessLevelButton = useMemo(() => {
    return (
      <Button
        hidden={hasPendingRequest}
        icon={PlayIcon}
        icon_position="left"
        on_click={() => setShowRequestLiveModeModal(true)}
        right="small"
        variant="secondary"
      >
        {currentEnvironmentAccessLevel === EnvironmentAccessLevel.NONE ? 'Request access' : 'Request live access'}
      </Button>
    );
  }, [currentEnvironmentAccessLevel, setShowRequestLiveModeModal, hasPendingRequest]);

  return (
    <Card {...props}>
      <Accordion icon_position="right" variant="plain">
        <Accordion.Header className={style['AccordionHeader']}>
          <Space className={style['AccordionTitle']}>
            <Div bottom="small">
              <P weight="bold">
                {entitlement.api.name}
                {hasPendingRequest && <Badge content="Access pending" left="small" />}
              </P>
              {currentEnvironmentAccessLevel === EnvironmentAccessLevel.NONE && (
                <Span slant="italic" weight="regular">
                  No access, need to request access
                </Span>
              )}
            </Div>
            <div>
              <ApiTypeBadge type={entitlement.api.type} />
            </div>
          </Space>
        </Accordion.Header>

        <Accordion.Content left="small" right="small" top="0">
          <P bottom="medium">{entitlement.api.description}</P>

          {entitlement?.scopeEntitlements.length === 0 && accessType !== 0 && (
            <ScopeCard
              environments={hasLiveAccess ? ['Test', 'Live'] : ['Test']}
              scopeDescription="Full access granted"
              scopeId=""
              scopeName="Full access"
            />
          )}

          {Object.keys(scopeEntitlementsByScopeName).map((scopeName) => {
            const entitlements = scopeEntitlementsByScopeName[scopeName]!;
            const environments = entitlements.map((ent) => (ent.liveMode ? 'Live' : 'Test'));
            const scopeEntitlement = entitlements[0]!;
            return (
              <ScopeCard
                environments={environments}
                key={scopeName}
                scopeDescription={scopeEntitlement.scopeDescription}
                scopeId={scopeEntitlement.scopeId}
                scopeName={scopeEntitlement.scopeName}
              />
            );
          })}

          {isAdmin && (
            <>
              <Space className={style['ContentContainer-Buttons']}>
                {entitlement.api.slug !== DEVELOPER_PORTAL_API_SLUG && (
                  <Dialog
                    confirmText="Detach"
                    description={
                      <>
                        This will remove the access to this {apiOrEvent + '. '}
                        <b>If you have any pending requests to this {apiOrEvent}, they will be canceled.</b>
                      </>
                    }
                    onConfirm={({ close }) => {
                      onDetachApi(app.id, entitlement.api.id);
                      close();
                    }}
                    title={`Are you sure you want to detach this ${apiOrEvent}?`}
                    triggerAttributes={{
                      text: `Detach ${apiOrEvent}`,
                      icon: TrashIcon,
                      icon_position: 'left',
                      variant: 'tertiary',
                    }}
                    variant="confirmation"
                  />
                )}
                {app.accessType === AccessTypes.O_AUTH_SYSTEM_TO_SYSTEM ? (
                  <Button
                    disabled={hasPendingRequest}
                    icon={EditIcon}
                    icon_position="left"
                    onClick={() => {
                      navigate(
                        app.owner.type === 'team'
                          ? `/team/${app.owner.id}/application/${app.id}/edit-api/${entitlement.api.id}`
                          : `/application/${app.id}/edit-api/${entitlement.api.id}`,
                      );
                    }}
                    right="small"
                    variant="tertiary"
                  >
                    Edit access
                  </Button>
                ) : (
                  canRequestHigherEnvironmentAccessLevel && requestHigherEnvironmentAccessLevelButton
                )}
              </Space>

              {showRequestLiveModeModal && (
                <RequestApiAccessModal
                  entitlement={entitlement}
                  onClose={() => {
                    setShowRequestLiveModeModal(false);
                    window.scrollTo(0, 0);
                  }}
                />
              )}
            </>
          )}
        </Accordion.Content>
      </Accordion>
    </Card>
  );
}
